import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import { getHistoryAsync, historyApply } from '../../modules/editor';
import { RootState } from '../../modules';

import ContentsList from '../Contents/ContentsList';
import useAlert from '../../hooks/useAlert';
import { DATETIME_FORMAT, DATE_FORMAT, TIME_FORMAT } from '../../constants';
import { HistoryModeProvider } from './HistoryContext';

dayjs.locale('ko');

type Props = {
	onClose: () => void;
};

const LIMIT = 20;

function History({ onClose }: Props) {
	const dispatch = useDispatch();
	const Alert = useAlert();

	const [previewContents, setPreviewContents] = useState([] as any);
	const [previewContainer, setPreviewContainer] = useState([] as any);
	const [activeIndex, setActiveIndex] = useState(0);
	const [isLoadingNewBunch, setIsLoadingNewBunch] = useState<boolean>(false);
	const [hasMore, setHasMore] = useState<boolean>(true);
	const [dateGroup, setDateGroup] = useState<string[] | undefined>(undefined);

	const offsetRef = useRef<number>(0);

	const emailId = useSelector((state: RootState) => state.editor.emailId);
	const history = useSelector((state: RootState) => state.editor.history);
	const emailType = useSelector((state: RootState) => state.editor.email.type);
	const emailSubType = useSelector((state: RootState) => state.editor.email.subType);
	const isContentsAbtest = emailType === 2 && emailSubType === 4;
	const testGroup = useSelector((state: RootState) => state.editor.testGroup);

	const contentListStyle = {
		background: previewContainer.background,
		borderWidth: previewContainer.borderWidth,
		borderStyle: previewContainer.borderStyle,
		borderColor: previewContainer.borderColor,
	}

	const loadHistoryBunch = () => {
		setIsLoadingNewBunch(true);
		dispatch(getHistoryAsync.request({ emailId, offset: offsetRef.current }));
	}

    useEffect(() => {
        loadHistoryBunch();
    }, []);


	useEffect(() => {
		const { total, offset, items } = history;
		const isFirstLoad = history.offset === 0;

		if (isLoadingNewBunch) {
			if (isFirstLoad) {
				previewShow(items[0], 0);
				if (total <= 20) setHasMore(false);
			};
			if (!isFirstLoad && offset >= total) {
				Alert.log('더 이상 없습니다.');
				setHasMore(false);
			}
			setIsLoadingNewBunch(false);
		}
	}, [history]);

	const parseHistoryItems = useMemo(() => {
		const dateArray: string[] = [];
		history.items?.map((item) => {
			dateArray.push(dayjs(item.lastModifiedTime).format(DATE_FORMAT));
			item['dateGroup'] = dayjs(item.lastModifiedTime).format(DATE_FORMAT);
		});
		const uniqueDateArray = [...new Set(dateArray)];
		setDateGroup(uniqueDateArray);
	}, [history.items]);

	const previewShow = (item: any, index:number) => {
		setPreviewContents(item.contents);
		setPreviewContainer(item.container);
		setActiveIndex(index);
	}

	const applyPreview = () => {
		Alert.confirm(Alert?.msg?.HISTORY_RESTORE, () => {
			const selectedItem = history.items.find((item, index) => {
				return index === activeIndex;
			});
			
			dispatch(historyApply(selectedItem));
			onClose();
		})
	}

	const onDrop = () => {}

	const onClickMore = () => {
		offsetRef.current = offsetRef.current + LIMIT;
		loadHistoryBunch();
	}
	
	return (
		<div className="history-modal">
			<div className="history history-modal-body">
				<header className="history-header">
					<div className='title'>
						<h1>과거 기록</h1>
						{
							isContentsAbtest && <span className='contents-abtest'>테스트 그룹 {testGroup}</span>
						}
					</div>
					<div className="btns">
						{
							activeIndex !== 0 && (
								<button
									className="btn medium secondary"
									onClick={() => { applyPreview() }}
								>이 버전으로 돌아가기</button>
								)
							}
						<button onClick={onClose} className="btn medium">닫기</button>
					</div>
				</header>
				<div className="history-content">
					<div className="history-preview" style={{ background: previewContainer.backgroundFull }}>
						<div className="content-list history-preview-inner">
							{
								previewContents.length !== 0 && (
									<div
										className="content-list"
										style={contentListStyle}
									>
										<HistoryModeProvider>
											<ContentsList
												list={previewContents}
												container={previewContainer}
												onDrop={onDrop}
											/>
										</HistoryModeProvider>
									</div>
								)
							}
						</div>
					</div>
					<div className="history-list">
						{
							dateGroup?.map((date) => (
								<ul key={date}>
									<div className='date-group'>{date}</div>
									{
										history.items?.map((item, index) => {
											if (item.dateGroup === date) {
												return <li
													key={index}
													onClick={() => { previewShow(item, index) }}
													className={activeIndex === index ? 'active' : ''}
												>
													<div className='time-line-icon'>
														<div className='rect' />
														<div className='circle' />
													</div>
													{dayjs(item.lastModifiedTime).format(TIME_FORMAT)}
													{
														index === 0 && (
															<span className="current">현재 버전</span>
														)
													}
												</li>
											}
										})
									}
								</ul>
							))
						}
						{
							hasMore &&
							<div className='more-btn'>
								<button className="btn medium" onClick={onClickMore}>더보기</button>
							</div>
						}
					</div>
				</div>
			</div>
		</div>
	);
}

// 2020. 8. 27. 오후 2:19 

export default History;   